import { fabric } from 'fabric';
import { AlignCenterHorizontal, AlignCenterVertical } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toFiniteNumber } from 'shared/utils';
import alignment from 'ud-ui/components/fabric-canvas/addons/alignment/alignment';
import UDTippy from 'ud-ui/components/tippy';
import { round } from '../../../../domain/utils/helpers';
import { selectActiveObjects, selectFabricManager } from '../../../../store/editor/selectors';
import Input, { IconWrapper } from '../../elements/input';
import LayerControls from './components/layer-controls';
import LockPosition from './components/lock-position';
import UDEmptyButton from 'modules/ud-ui/components/empty-button';
import { Item, LongDivider, ShortDivider, SubItem } from './styles';

function Image() {
  const activeObjects = useSelector(selectActiveObjects);
  const fabricManager = useSelector(selectFabricManager);
  const object = activeObjects[0];

  const scale = object?.getObjectScaling();
  const { width, height, top, left } = object?.getBoundingRect(true) ?? defaultBounds;
  const lockMovementX = object?.lockMovementX;

  const [position, setPosition] = useState<{ x: number; y: number }>({ x: round(left), y: round(top) });
  const [size, setSize] = useState<{ width: number; height: number }>({ width: round(width), height: round(height) });
  const [isPositionLock, setIsPositionLock] = useState(lockMovementX ?? false);

  useEffect(() => {
    setPosition({ x: round(left), y: round(top) });
    setSize({ width: round(width), height: round(height) });
    setIsPositionLock(lockMovementX ?? false);

    let updatePosition = () => {
      const x = object?.get('left') ?? 0;
      const y = object?.get('top') ?? 0;
      setPosition({ x: round(x), y: round(y) });
    };

    let updateSize = () => {
      const { width, height } = object?.getBoundingRect(true) ?? defaultBounds;
      setSize({ width: round(width), height: round(height) });
    };

    fabricManager?.canvas.on('mouse:up', updatePosition);
    fabricManager?.canvas.on('mouse:up', updateSize);

    return () => {
      fabricManager?.canvas.off('mouse:up', updatePosition);
      fabricManager?.canvas.off('mouse:up', updateSize);
    };

    // eslint-disable-next-line
  }, [activeObjects]);

  const onChangePosition = (x: number, y: number) => {
    object?.setPositionByOrigin(new fabric.Point(x, y), 'left', 'top');
    setPosition({ x, y });
    fabricManager?.canvas.requestRenderAll();
  };

  const onChangeSize = (width: number, height: number) => {
    object?.set({ width: width / (scale?.scaleX ?? 1), height: height / (scale?.scaleY ?? 1) });
    setSize({ width, height });
    fabricManager?.canvas.requestRenderAll();
  };

  const onClickAlignmentCenterVertical = () => {
    const newPos = alignment.single.centerVertical(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const onClickAlignmentCenterHorizontal = () => {
    const newPos = alignment.single.centerHorizontal(object);
    if (!newPos) return;
    setPosition({ x: newPos.left, y: newPos.top });
  };

  const showDebugInfo = false;
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <>
      <Item>
        <div>Изображение</div>
      </Item>
      <ShortDivider />
      <Item>
        <SubItem style={{ width: '26%', justifyContent: 'space-between' }}>
          <UDTippy content="По центру по горизонтали" size="xs" placement="bottom" delay={300}>
            <span style={{ display: 'inline-block' }}>
              <UDEmptyButton onClick={onClickAlignmentCenterHorizontal}>
                <AlignCenterHorizontal size={18} color={'#007AFF'} />
              </UDEmptyButton>
            </span>
          </UDTippy>
          <UDTippy content="По центру по вертикали" size="xs" placement="bottom" delay={300}>
            <span style={{ display: 'inline-block' }}>
              <UDEmptyButton onClick={onClickAlignmentCenterVertical}>
                <AlignCenterVertical size={18} color={'#007AFF'} />
              </UDEmptyButton>
            </span>
          </UDTippy>
        </SubItem>
        <SubItem>
          <span>Расположение</span>
          <Input
            numeric
            style={{ width: 73 }}
            icon={<IconWrapper>X</IconWrapper>}
            value={position.x.toString()}
            onChange={(value) => onChangePosition(toFiniteNumber(value, position.x), position.y)}
          />
          <Input
            numeric
            style={{ width: 73 }}
            icon={<IconWrapper>Y</IconWrapper>}
            value={position.y.toString()}
            onChange={(value) => onChangePosition(position.x, toFiniteNumber(value, position.y))}
          />
        </SubItem>
        <SubItem>
          <span>Размер</span>
          <Input
            numeric
            icon={<IconWrapper>Ш</IconWrapper>}
            value={size.width.toString()}
            onChange={(value) => onChangeSize(toFiniteNumber(value, size.width), size.height)}
          />
          <Input
            numeric
            icon={<IconWrapper>В</IconWrapper>}
            value={size.height.toString()}
            onChange={(value) => onChangeSize(size.width, toFiniteNumber(value, size.height))}
          />
        </SubItem>
      </Item>
      <LongDivider />
      <LayerControls object={object} />
      <LockPosition objects={[object]} onChange={setIsPositionLock} value={isPositionLock} />
      {isDev && showDebugInfo && (
        <>
          <LongDivider />
          <Item>
            <div>Отладка</div>
            <span>{JSON.stringify({ width: object.getScaledWidth(), height: object.getScaledHeight() }, null, 2)}</span>
            <span style={{ whiteSpace: 'pre' }}>{JSON.stringify(object?.toObject(), null, 2)}</span>
          </Item>
        </>
      )}
    </>
  );
}

export default Image;

const defaultBounds = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};
